/**
 * UserContext - Centralized user data and kundli state management
 * Phase 3 Refactoring: All user/chart logic lives here
 * 
 * Handles:
 * - userInfo state (merged from auth + guest data)
 * - birthData (derived from guest OR logged-in profile)
 * - kundliData, divisionalChartsData
 * - Chart calculation triggers
 * - Loading states
 * 
 * App.js only consumes: userInfo, kundliData, isChartLoading, hasValidBirthData
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { getBackendUrl } from '../config/api';
import { classifySaveFailure, SESSION_EXPIRED_MESSAGE } from '../utils/birthPlace';
import { setAuthNotice } from '../utils/authNotice';
import { parseTime } from '../utils/birthTime';

// Create axios instance with dynamic base URL
const createApi = () => {
  const instance = axios.create({
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json'
    }
  });
  
  // Add request interceptor to set baseURL at runtime
  instance.interceptors.request.use((config) => {
    config.baseURL = `${getBackendUrl()}/api`;
    return config;
  });
  
  return instance;
};

const publicApi = createApi();

const UserContext = createContext(null);

export const useUser = () => {
  const context = useContext(UserContext);
  // Allow null context during migration for backward compatibility
  return context;
};

// Helper function to get sign name
const getSignName = (signIndex) => {
  const signs = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  return signs[signIndex - 1] || 'Unknown';
};

// Enhanced date parsing with natural language support
const parseDate = (dateInput) => {
  if (!dateInput) return '';
  const trimmed = dateInput.trim().toLowerCase();
  
  // Standard DD-MM-YYYY format
  const standardFormat = /^\d{1,2}-\d{1,2}-\d{4}$/;
  if (standardFormat.test(trimmed)) {
    return trimmed;
  }

  // DD/MM/YYYY format
  const slashFormat = /^\d{1,2}\/\d{1,2}\/\d{4}$/;
  if (slashFormat.test(trimmed)) {
    return trimmed.replace(/\//g, '-');
  }

  // Try to parse natural language dates
  try {
    const parsedDate = new Date(dateInput);
    if (!isNaN(parsedDate.getTime())) {
      const day = parsedDate.getDate().toString().padStart(2, '0');
      const month = (parsedDate.getMonth() + 1).toString().padStart(2, '0');
      const year = parsedDate.getFullYear();
      return `${day}-${month}-${year}`;
    }
  } catch (e) {
    console.warn('[USER] Natural date parsing failed:', e);
  }
  
  return trimmed;
};

export const UserProvider = ({ children }) => {
  // Get auth context
  const auth = useAuth();
  const authUser = auth?.user || null;
  const isAuthenticated = auth?.isAuthenticated || false;
  const justAuthenticated = auth?.justAuthenticated || false;
  const authLoading = auth?.authLoading ?? true;  // Default to true if not available
  const authChecked = auth?.authChecked ?? false;
  
  // Check for test mode
  const testMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('test') === 'true';
  }, []);
  
  // Guest birth data from session storage
  const getGuestBirthData = useCallback(() => {
    try {
      const data = sessionStorage.getItem('guestBirthData');
      if (!data) return null;
      return JSON.parse(data);
    } catch {
      return null;
    }
  }, []);
  
  const [guestBirthData, setGuestBirthData] = useState(getGuestBirthData);
  
  // User info state (merged from auth user and guest data)
  const [userInfo, setUserInfo] = useState(null);
  
  // Kundli data
  const [kundliData, setKundliData] = useState(null);
  const [divisionalChartsData, setDivisionalChartsData] = useState(null);
  
  // Loading states
  const [isChartLoading, setIsChartLoading] = useState(false);
  const [chartCalculationAttempted, setChartCalculationAttempted] = useState(false);
  const [chartError, setChartError] = useState(null);
  
  // Computed: has valid birth data from any source
  const hasValidBirthData = useMemo(() => {
    return !!(userInfo?.date && userInfo?.time && userInfo?.place);
  }, [userInfo]);
  
  // Computed: birth data (derived from guest OR logged-in profile)
  const birthData = useMemo(() => {
    if (!userInfo) return null;
    return {
      date: userInfo.date,
      time: userInfo.time,
      place: userInfo.place,
      latitude: userInfo.latitude,
      longitude: userInfo.longitude,
      name: userInfo.name,
      gender: userInfo.gender,
    };
  }, [userInfo]);
  
  // Computed: effective user (for backward compatibility)
  const effectiveUser = useMemo(() => {
    if (authUser?.onboarding_complete && authUser?.date && authUser?.time && authUser?.place) {
      return authUser;
    }
    return null;
  }, [authUser]);
  
  /**
   * Calculate kundli from birth details
   * This is the main chart calculation function
   */
  const calculateKundli = useCallback(async (birthDetails) => {
    if (!birthDetails?.date || !birthDetails?.time || !birthDetails?.place) {
      console.log('[USER] Missing birth data for kundli calculation');
      return null;
    }
    
    setIsChartLoading(true);
    setChartCalculationAttempted(true);
    setChartError(null);
    
    try {
      console.log('[USER] Calculating kundli for:', birthDetails.name);
      
      const parsedDate = parseDate(birthDetails.date);
      const parsedTime = parseTime(birthDetails.time);
      
      const processedBirthDetails = {
        name: birthDetails.name?.trim() || 'User',
        date: parsedDate,
        time: parsedTime,
        place: birthDetails.place?.trim() || '',
        gender: birthDetails.gender || 'unknown',
        latitude: birthDetails.latitude,
        longitude: birthDetails.longitude,
      };
      
      console.log('[USER] 📅 Processed birth details:', processedBirthDetails);
      
      // Use divisional charts API for all chart data to ensure consistency
      // Using publicApi instance to avoid CORS issues
      const divisionalResponse = await publicApi.post('/divisional-charts-extended', processedBirthDetails);
      const divisionalResponseData = divisionalResponse.data;
      
      // Validate response structure
      if (!divisionalResponseData || !divisionalResponseData.charts || !divisionalResponseData.charts.D1) {
        throw new Error('Invalid API response: Missing chart data structure');
      }
      
      // Use D1 chart from divisional API as the main kundli data for consistency
      const d1Chart = divisionalResponseData.charts.D1;
      console.log('[USER] 🔍 D1 chart extracted, planets count:', d1Chart.planets?.length);
      console.log('[USER] 🔍 D1 chart ascendant:', d1Chart.ascendant_sign);
      
      // Validate essential chart data
      if (!d1Chart.planets || !Array.isArray(d1Chart.planets) || d1Chart.planets.length === 0) {
        throw new Error('Invalid chart data: Missing or empty planets array');
      }
      
      // CRITICAL: Validate ascendant_sign is valid (1-12)
      const ascendantSign = d1Chart.ascendant_sign;
      if (!ascendantSign || ascendantSign < 1 || ascendantSign > 12) {
        console.error('[USER] ❌ INVALID ascendant_sign from API:', ascendantSign);
        throw new Error(`Invalid ascendant sign: ${ascendantSign}`);
      }
      
      // CRITICAL: Generate house_signs from ascendant if not provided or invalid
      // House 1 = Ascendant sign, House 2 = next sign, etc.
      let house_signs_data;
      if (d1Chart.house_signs && Array.isArray(d1Chart.house_signs) && d1Chart.house_signs.length === 12) {
        console.log('[USER] ✅ Using house_signs from API:', d1Chart.house_signs);
        house_signs_data = d1Chart.house_signs.map((signIndex, i) => ({
          house: i + 1,
          sign_index: signIndex,
          sign_name: getSignName(signIndex)
        }));
      } else {
        // FALLBACK: Calculate house signs from ascendant (CORRECT FORMULA)
        console.warn('[USER] ⚠️ Calculating house_signs from ascendant:', ascendantSign);
        house_signs_data = Array.from({length: 12}, (_, i) => {
          // House 1 = ascendant sign, House 2 = ascendant + 1, etc.
          const signIndex = ((ascendantSign - 1 + i) % 12) + 1;
          return {
            house: i + 1,
            sign_index: signIndex,
            sign_name: getSignName(signIndex)
          };
        });
        console.log('[USER] 🔧 Generated house_signs:', house_signs_data.map(h => h.sign_index));
      }
      
      // Transform divisional chart D1 data to match traditional kundli format
      const kundliResponseData = {
        planets: d1Chart.planets?.map(planet => ({
          ...planet,
          degrees_in_sign: Math.floor(planet.degree_in_sign || planet.degrees_in_sign || 0),
          minutes_in_sign: Math.floor(((planet.degree_in_sign || planet.degrees_in_sign || 0) % 1) * 60)
        })) || [],
        ascendant_sign_index: ascendantSign,
        ascendant_sign_name: getSignName(ascendantSign),
        ascendant_degrees: Math.floor(d1Chart.ascendant_degree || 0),
        ascendant_minutes: Math.floor(((d1Chart.ascendant_degree || 0) % 1) * 60),
        ascendant_nakshatra: d1Chart.ascendant_nakshatra || 'Unknown',
        ascendant_longitude: d1Chart.ascendant_degree || 0,
        house_signs: house_signs_data,
        house_cusps: Array.from({length: 12}, (_, i) => ({house: i + 1, degrees: 0}))
      };
      
      console.log('[USER] ✅ Unified chart calculation successful');
      setKundliData(kundliResponseData);
      setDivisionalChartsData(divisionalResponseData);
      
      return kundliResponseData;
      
    } catch (error) {
      console.error('[USER] ❌ Chart calculation failed:', error);
      
      let errorMessage = 'Birth chart calculation failed!';
      if (error.response) {
        errorMessage += ` Server responded with ${error.response.status}: ${error.response.data?.detail || error.response.statusText}`;
      } else if (error.request) {
        errorMessage += ' No response from server. Check if backend is running.';
      } else {
        errorMessage += ` Request setup error: ${error.message}`;
      }
      
      setChartError(errorMessage);
      return null;
      
    } finally {
      setIsChartLoading(false);
    }
  }, []);
  
  /**
   * Complete onboarding with birth details
   * Called when user submits onboarding form
   */
  const completeOnboarding = useCallback(async (formData) => {
    console.log('[USER] completeOnboarding called with:', formData.name);
    
    // IMPORTANT: Set this FIRST to prevent race condition with auto-calculate effect
    setChartCalculationAttempted(true);
    setIsChartLoading(true);
    
    const parsedDate = parseDate(formData.date);
    const parsedTime = parseTime(formData.time);
    
    const birthDetails = {
      name: formData.name?.trim() || 'User',
      date: parsedDate,
      time: parsedTime,
      place: formData.place?.trim() || '',
      gender: formData.gender || 'unknown',
      latitude: formData.latitude,
      longitude: formData.longitude,
    };
    
    // JP-118: decide on the presence of a valid session token, NOT on the
    // possibly-stale auth context. During onboarding, CosmicOnboarding writes
    // the token synchronously but the AuthContext re-render may not have landed
    // in this closure yet — reading isAuthenticated here took the guest branch
    // and minted an orphan guest_ doc AFTER registration. A token in
    // localStorage is the authoritative "this user is registered" signal.
    // A failed save (any non-2xx, or no response) STOPS here: no userInfo, no
    // chart for details the server did not accept (e.g. an unmapped place,
    // 422 place_unresolved, or a busy geocoder, 503 place_check_busy). The
    // caller keeps the form open and says why (saveFailureMessage).
    // chartCalculationAttempted stays TRUE: resetting it re-arms both the
    // auto-calc effect below and App's guest-data effect, which would re-send
    // the same rejected details (repeated saves + toasts). A new submit from
    // the form calls completeOnboarding again, which is the only retry.
    const stopOnSaveFailure = (failure) => {
      setIsChartLoading(false);
      return { success: false, userInfo: null, kundliData: null, ...failure };
    };

    const sessionToken = localStorage.getItem('jyotish_session_token');
    if (auth?.isAuthenticated || sessionToken) {
      // Authenticated user: save to their own account via the session-scoped
      // endpoint — never generate a guest_ id or create a guest doc
      try {
        const token = sessionToken;
        const response = await fetch(`${getBackendUrl()}/api/auth/complete-onboarding`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            name: birthDetails.name,
            gender: birthDetails.gender,
            date: birthDetails.date,
            time: birthDetails.time,
            place: birthDetails.place,
            latitude: birthDetails.latitude,
            longitude: birthDetails.longitude
          }),
        });
        if (response.ok) {
          // The save succeeded; an empty or odd 2xx body must not turn it
          // into "Failed to save" — it only means no user object to merge.
          const data = await response.json().catch(() => ({}));
          if (data?.user) {
            auth.setUser?.(data.user);
            birthDetails.user_id = data.user.user_id || data.user.id;
          }
          console.log('[USER] Onboarding saved to authenticated profile');
        } else if (response.status === 401) {
          // The session is gone (expired / revoked). Retrying can never
          // succeed, so no "try again" toast: clear auth and go to login.
          console.warn('[USER] complete-onboarding: session expired');
          // logout (performLogout) clears sessionStorage, so the return path
          // and the login-page notice are written AFTER it, not before.
          const returnPath = window.location.pathname;
          try { await auth?.logout?.(); } catch {}
          try { sessionStorage.setItem('authReturnPath', returnPath); } catch {}
          setAuthNotice(SESSION_EXPIRED_MESSAGE);
          window.location.assign('/login');
          return stopOnSaveFailure({ saveFailed: true, sessionExpired: true, placeUnresolved: false, placeCheckBusy: false, status: 401 });
        } else {
          console.warn('[USER] complete-onboarding failed with status:', response.status);
          const body = await response.json().catch(() => ({}));
          return stopOnSaveFailure({ ...classifySaveFailure(body), status: response.status });
        }
      } catch (saveError) {
        console.warn('[USER] Failed to save onboarding to profile:', saveError);
        return stopOnSaveFailure(classifySaveFailure(saveError));
      }
    } else {
      // Guest path (unchanged): generate/get guest user ID
      let guestUserId = localStorage.getItem('guest_user_id');
      if (!guestUserId) {
        guestUserId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('guest_user_id', guestUserId);
      }

      // Save user profile to database
      try {
        await publicApi.post('/user-profile', {
          user_id: guestUserId,
          name: birthDetails.name,
          gender: birthDetails.gender,
          date: birthDetails.date,
          time: birthDetails.time,
          place: birthDetails.place,
          latitude: birthDetails.latitude,
          longitude: birthDetails.longitude
        });
        console.log('[USER] Guest user saved to database:', guestUserId);
        birthDetails.user_id = guestUserId;
      } catch (saveError) {
        console.warn('[USER] Failed to save guest user:', saveError);
        return stopOnSaveFailure({ ...classifySaveFailure(saveError), status: saveError?.response?.status });
      }
    }
    
    // Set userInfo AFTER setting chartCalculationAttempted to prevent race condition
    setUserInfo(birthDetails);
    
    // Store in sessionStorage for persistence
    sessionStorage.setItem('guestBirthData', JSON.stringify(birthDetails));
    setGuestBirthData(birthDetails);
    
    // Calculate kundli - this will handle its own loading state
    const result = await calculateKundli(birthDetails);
    
    return {
      success: !!result,
      userInfo: birthDetails,
      kundliData: result,
    };
  }, [calculateKundli, auth]);
  
  /**
   * Save guest birth data to session storage
   */
  const saveGuestBirthData = useCallback((data) => {
    sessionStorage.setItem('guestBirthData', JSON.stringify(data));
    setGuestBirthData(data);
    console.log('[USER] Guest birth data saved');
  }, []);
  
  /**
   * Clear guest birth data
   */
  const clearGuestBirthData = useCallback(() => {
    sessionStorage.removeItem('guestBirthData');
    setGuestBirthData(null);
    console.log('[USER] Guest birth data cleared');
  }, []);
  
  /**
   * Refresh user profile from backend
   */
  const refreshUserProfile = useCallback(async () => {
    console.log('[USER] Refreshing user profile...');
    
    // If authenticated, fetch from auth
    if (auth?.fetchCurrentUser) {
      await auth.fetchCurrentUser();
    }
    
    // Recalculate kundli if we have userInfo
    if (userInfo && hasValidBirthData) {
      setChartCalculationAttempted(false);
      await calculateKundli(userInfo);
    }
  }, [auth, userInfo, hasValidBirthData, calculateKundli]);
  
  /**
   * Update user profile
   */
  const updateProfile = useCallback((updates) => {
    setUserInfo(prev => prev ? { ...prev, ...updates } : null);
    
    // Clear cached kundli data if birth details changed
    if (updates.date || updates.time || updates.place) {
      setKundliData(null);
      setDivisionalChartsData(null);
      setChartCalculationAttempted(false);
    }
  }, []);
  
  /**
   * Clear all user data (on logout)
   */
  const clearUserData = useCallback(() => {
    setUserInfo(null);
    setKundliData(null);
    setDivisionalChartsData(null);
    setChartCalculationAttempted(false);
    setChartError(null);
    clearGuestBirthData();
  }, [clearGuestBirthData]);
  
  // Effect: Merge userInfo from auth and guest data
  useEffect(() => {
    let newUserInfo = null;
    
    // Priority 1: Authenticated user with complete data
    if (authUser?.onboarding_complete && authUser?.date && authUser?.time && authUser?.place) {
      console.log('[USER] Using authenticated user data');
      newUserInfo = {
        name: authUser.name,
        gender: authUser.gender,
        date: authUser.date,
        time: authUser.time,
        place: authUser.place,
        latitude: authUser.latitude,
        longitude: authUser.longitude,
        needs_birthplace: !!authUser.needs_birthplace,
        user_id: authUser.user_id,
        email: authUser.email,
        picture: authUser.picture,
        subscription_tier: authUser.subscription_tier,
      };
    }
    // Priority 2: Authenticated user + guest birth data (user signed in during onboarding)
    else if (authUser && guestBirthData?.date && guestBirthData?.time && guestBirthData?.place) {
      console.log('[USER] Using guest birth data with auth user info');
      newUserInfo = {
        name: guestBirthData.name || authUser?.name || 'User',
        gender: guestBirthData.gender,
        date: guestBirthData.date,
        time: guestBirthData.time,
        place: guestBirthData.place,
        latitude: guestBirthData.latitude,
        longitude: guestBirthData.longitude,
        user_id: authUser?.user_id,
        email: authUser?.email,
        picture: authUser?.picture,
      };
    }
    // Priority 3: Guest birth data only
    else if (guestBirthData?.date && guestBirthData?.time && guestBirthData?.place) {
      console.log('[USER] Using guest birth data');
      newUserInfo = {
        name: guestBirthData.name || 'User',
        gender: guestBirthData.gender,
        date: guestBirthData.date,
        time: guestBirthData.time,
        place: guestBirthData.place,
        latitude: guestBirthData.latitude,
        longitude: guestBirthData.longitude,
        user_id: guestBirthData.user_id,
      };
    }
    // Priority 4: Test mode with mock data
    else if (testMode) {
      console.log('[USER] Using test mode data');
      // Use a stable test user_id stored in localStorage to persist across sessions
      let testUserId = localStorage.getItem('test_user_id');
      if (!testUserId) {
        testUserId = 'test_user_' + Date.now();
        localStorage.setItem('test_user_id', testUserId);
      }
      newUserInfo = {
        name: 'Test User',
        gender: 'Male',
        date: '03-05-1991',
        time: '08:05 AM',
        place: 'Bhopal',
        user_id: testUserId
      };
    }
    
    if (newUserInfo && JSON.stringify(newUserInfo) !== JSON.stringify(userInfo)) {
      console.log('[USER] Setting userInfo:', newUserInfo.name);
      setUserInfo(newUserInfo);
    }
  }, [authUser, guestBirthData, testMode, userInfo]);
  
  // Effect: Auto-calculate kundli when userInfo is set and no kundli data exists
  // IMPORTANT: Wait for auth to be checked first to prevent cascade of failed API calls
  useEffect(() => {
    // Don't attempt chart calculation while auth is still loading
    if (authLoading) {
      return;
    }
    
    if (userInfo && !kundliData && !chartCalculationAttempted && !isChartLoading) {
      console.log('[USER] Auto-calculating kundli for:', userInfo.name);
      calculateKundli(userInfo);
    }
  }, [userInfo, kundliData, chartCalculationAttempted, isChartLoading, calculateKundli, authLoading]);
  
  // Effect: Clear stale guest data on page load if not in auth flow
  useEffect(() => {
    // Only clear if not just authenticated and no auth user
    if (!justAuthenticated && !authUser && !testMode) {
      // Check if guest data is stale (more than 1 hour old)
      const guestData = getGuestBirthData();
      if (guestData?.savedAt) {
        const savedTime = new Date(guestData.savedAt).getTime();
        const oneHour = 60 * 60 * 1000;
        if (Date.now() - savedTime > oneHour) {
          console.log('[USER] Clearing stale guest birth data');
          clearGuestBirthData();
        }
      }
    }
  }, [justAuthenticated, authUser, testMode, getGuestBirthData, clearGuestBirthData]);

  const value = {
    // State
    userInfo,
    kundliData,
    divisionalChartsData,
    isChartLoading,
    chartCalculationAttempted,
    chartError,
    guestBirthData,
    testMode,
    
    // Computed
    hasValidBirthData,
    hasUserInfo: !!userInfo,
    hasKundliData: !!kundliData,
    birthData,
    effectiveUser,
    
    // Setters (for backward compatibility during migration)
    setUserInfo,
    setKundliData,
    setDivisionalChartsData,
    
    // Actions
    calculateKundli,
    completeOnboarding,
    saveGuestBirthData,
    clearGuestBirthData,
    refreshUserProfile,
    updateProfile,
    clearUserData,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
